// Pure logic: conditional visibility, validation, and wire-keyed answer building.

import {
  Answers,
  DescriptionItem,
  FieldItem,
  FormDefinition,
  Item,
  Option,
  OTHER,
  Rule,
  ShowWhen,
  Step,
} from './types';

/** Flatten every field in the definition, in document order. */
export function allFields(def: FormDefinition): FieldItem[] {
  const out: FieldItem[] = [];
  const walk = (items: Item[]) => {
    for (const it of items) {
      if (it.kind === 'field') out.push(it);
      else if (it.kind === 'section') walk(it.items);
    }
  };
  for (const step of def.steps) walk(step.items);
  return out;
}

/** Flatten every description item in the definition, in document order. */
export function allDescriptions(def: FormDefinition): DescriptionItem[] {
  const out: DescriptionItem[] = [];
  const walk = (items: Item[]) => {
    for (const it of items) {
      if (it.kind === 'description') out.push(it);
      else if (it.kind === 'section') walk(it.items);
    }
  };
  for (const step of def.steps) walk(step.items);
  return out;
}

/** Flatten every field within one step, in document order. */
export function stepFields(step: Step): FieldItem[] {
  const out: FieldItem[] = [];
  const walk = (items: Item[]) => {
    for (const it of items) {
      if (it.kind === 'field') out.push(it);
      else if (it.kind === 'section') walk(it.items);
    }
  };
  walk(step.items);
  return out;
}

function ruleHit(rule: Rule, answers: Answers, otherText: Record<string, string>): boolean {
  const v = answers[rule.field];
  let hit = false;
  if (Array.isArray(v)) {
    hit = v.includes(rule.value);
  } else if (typeof v === 'string' && v !== '') {
    hit = v === rule.value || (v === OTHER && (otherText[rule.field] ?? '') === rule.value);
  }
  return rule.op === 'is' ? hit : !hit;
}

export function passes(
  sw: ShowWhen | undefined,
  answers: Answers,
  otherText: Record<string, string>,
): boolean {
  if (!sw || sw.rules.length === 0) return true;
  return sw.mode === 'any'
    ? sw.rules.some((r) => ruleHit(r, answers, otherText))
    : sw.rules.every((r) => ruleHit(r, answers, otherText));
}

export interface Visibility {
  /** ids of every visible item (fields, sections, descriptions). */
  visibleItems: Set<string>;
  /** answers restricted to visible fields (hidden answers excluded). */
  effectiveAnswers: Answers;
}

/**
 * Compute item visibility across the whole form. An item is visible iff its own
 * showWhen passes AND every enclosing section's showWhen passes. Hidden fields'
 * answers are excluded from rule evaluation, so visibility is computed to a
 * fixpoint (a rule chained off a hidden field cannot keep anything visible).
 */
export function computeVisibility(
  def: FormDefinition,
  answers: Answers,
  otherText: Record<string, string>,
): Visibility {
  const fields = allFields(def);
  let effective: Answers = { ...answers };
  let visibleItems = new Set<string>();
  let prevKey = '';

  for (let pass = 0; pass < 10; pass++) {
    const vis = new Set<string>();
    const walk = (items: Item[], parentOk: boolean) => {
      for (const it of items) {
        const ok = parentOk && passes(it.showWhen, effective, otherText);
        if (ok) vis.add(it.id);
        if (it.kind === 'section') walk(it.items, ok);
      }
    };
    for (const step of def.steps) walk(step.items, true);

    const nextEffective: Answers = {};
    for (const f of fields) {
      if (vis.has(f.id) && answers[f.id] !== undefined) nextEffective[f.id] = answers[f.id];
    }
    visibleItems = vis;
    effective = nextEffective;

    const key = [...vis].sort().join('|');
    if (key === prevKey) break;
    prevKey = key;
  }

  return { visibleItems, effectiveAnswers: effective };
}

/** Initial answers: apply field defaults. */
export function initialAnswers(def: FormDefinition): Answers {
  const answers: Answers = {};
  for (const f of allFields(def)) {
    if (f.default !== undefined) answers[f.id] = f.default;
  }
  return answers;
}

/**
 * Apply the definition's URL-parameter prefill map. A parameter value counts
 * only when the target field validates it cleanly; returns the set of field
 * ids that were prefilled (their screens are skipped in conversational flow).
 */
export function applyPrefill(
  def: FormDefinition,
  answers: Answers,
  search: string,
): Set<string> {
  const prefilled = new Set<string>();
  if (!def.prefill) return prefilled;
  const params = new URLSearchParams(search);
  const fields = allFields(def);
  for (const [fieldId, param] of Object.entries(def.prefill)) {
    const field = fields.find((f) => f.id === fieldId);
    const raw = params.get(param)?.trim();
    if (!field || !raw) continue;
    const value = field.type === 'checkbox' ? [raw] : raw;
    const errs = validateFields([field], { [fieldId]: value }, {}, {});
    if (Object.keys(errs).length === 0) {
      answers[fieldId] = value;
      prefilled.add(fieldId);
    }
  }
  return prefilled;
}

/** First success variant whose rules pass against the final answers, if any. */
export function pickSuccessVariant(
  def: FormDefinition,
  answers: Answers,
  otherText: Record<string, string>,
): { title?: string; html: string } | null {
  for (const variant of def.successVariants ?? []) {
    if (passes(variant.when, answers, otherText)) return variant;
  }
  return null;
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * The choices a field is offering right now. An option may carry its own
 * showWhen (Discard is not offered on print orders), so the list is answer
 * dependent. The Worker applies the same rule, so a hidden choice cannot be
 * submitted even by hand.
 */
export function liveOptions(
  f: FieldItem,
  answers: Answers,
  otherText: Record<string, string>,
): Option[] {
  const opts = f.options ?? [];
  if (!opts.some((o) => o.showWhen)) return opts;
  return opts.filter((o) => passes(o.showWhen, answers, otherText));
}

/** A copy of the field carrying only its currently offered options. */
export function fieldForRender(
  f: FieldItem,
  answers: Answers,
  otherText: Record<string, string>,
): FieldItem {
  if (!(f.options ?? []).some((o) => o.showWhen)) return f;
  return { ...f, options: liveOptions(f, answers, otherText) };
}

/**
 * Is this a phone number Square will accept?
 *
 * Deliberately permissive about FORMAT (spaces, dashes, brackets are fine) and
 * strict only about the things that make a number unusable:
 *   - "+" form: 8-15 digits and a country code that cannot start with 0
 *     (E.164 has no leading-zero country codes).
 *   - national form starting 0: exactly 10 digits, which every Australian
 *     number is. "089562347" is 9, and it cost us a looping order because
 *     Square rejects the customer outright and the form never checked.
 *   - anything else: 8-15 digits, so overseas numbers typed without a "+"
 *     still get through.
 */
export function isUsablePhone(raw: string): boolean {
  const s = raw.replace(/[\s().-]/g, '');
  if (/^\+/.test(s)) return /^\+[1-9]\d{7,14}$/.test(s);
  if (!/^\d+$/.test(s)) return false;
  if (s.startsWith('0')) return s.length === 10;
  return s.length >= 8 && s.length <= 15;
}

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

function toIso(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/**
 * The `min`/`max` a date input should offer, derived from the device's own
 * clock. The picker greys out everything outside the window, so a customer
 * cannot pick a job we no longer hold; `validateFields` re-checks typed input
 * (desktop date inputs accept keystrokes) and the Worker checks it again.
 */
export function dateBounds(f: FieldItem): { min?: string; max?: string } {
  if (f.type !== 'date') return {};
  const bounds: { min?: string; max?: string } = {};
  const today = new Date();
  if (f.minMonthsAgo !== undefined) {
    const floor = new Date(today);
    floor.setMonth(floor.getMonth() - f.minMonthsAgo);
    bounds.min = toIso(floor);
  }
  if (f.noFuture) bounds.max = toIso(today);
  return bounds;
}

/**
 * Validate a list of (visible) fields. Returns errors keyed by field id;
 * an email-confirm mismatch is keyed `<id>__confirm`.
 */
export function validateFields(
  fields: FieldItem[],
  answers: Answers,
  otherText: Record<string, string>,
  confirmEmail: Record<string, string>,
): Record<string, string> {
  const errors: Record<string, string> = {};

  for (const f of fields) {
    const v = answers[f.id];
    const isEmpty = Array.isArray(v)
      ? v.length === 0
      : v === undefined || (typeof v === 'string' && v.trim() === '');

    if (f.required && isEmpty) {
      errors[f.id] = 'This field is required.';
      continue;
    }
    if (isEmpty || Array.isArray(v) || typeof v !== 'string') continue;

    if (f.allowOther && v === OTHER && (otherText[f.id] ?? '').trim() === '') {
      errors[f.id] = 'Please type your answer next to Other.';
      continue;
    }

    if (f.type === 'email') {
      if (!EMAIL_RE.test(v.trim())) {
        errors[f.id] = 'Please enter a valid email address.';
      } else if (f.confirm) {
        const c = (confirmEmail[f.id] ?? '').trim();
        if (c === '') {
          errors[`${f.id}__confirm`] = 'Please confirm your email address.';
        } else if (c.toLowerCase() !== v.trim().toLowerCase()) {
          errors[`${f.id}__confirm`] = 'Email addresses do not match.';
        }
      }
    }

    if (f.type === 'number') {
      const n = Number(v);
      if (!Number.isFinite(n) || !Number.isInteger(n)) {
        errors[f.id] = 'Please enter a whole number.';
      } else if (f.min !== undefined && n < f.min) {
        errors[f.id] = `Please enter a number between ${f.min} and ${f.max ?? f.min}.`;
      } else if (f.max !== undefined && n > f.max) {
        errors[f.id] = `Please enter a number between ${f.min ?? 0} and ${f.max}.`;
      }
    }

    if (f.type === 'date') {
      const value = v.trim();
      // Component round-trip, not just a parse: an impossible day rolls over
      // silently (2026-02-30 becomes 2026-03-02) instead of failing.
      const [y, m, d] = value.split('-').map(Number);
      const parsed = DATE_RE.test(value) ? new Date(y, m - 1, d) : null;
      const valid =
        parsed !== null &&
        parsed.getFullYear() === y &&
        parsed.getMonth() === m - 1 &&
        parsed.getDate() === d;
      if (!valid) {
        errors[f.id] = 'Please enter a valid date.';
      } else {
        const bounds = dateBounds(f);
        if (bounds.max && value > bounds.max) {
          errors[f.id] = 'That date is in the future.';
        } else if (bounds.min && value < bounds.min) {
          errors[f.id] =
            `We can only retrieve jobs from the last ${f.minMonthsAgo} months. ` +
            'Please contact us if your job is older than that.';
        }
      }
    }

    // A choice that is no longer offered is not a valid answer: switching to
    // a print service after picking Discard must not silently keep Discard.
    if ((f.options ?? []).some((o) => o.showWhen) && v !== OTHER) {
      const live = liveOptions(f, answers, otherText);
      if (!live.some((o) => o.value === v)) {
        errors[f.id] = 'Please choose one of the available options.';
      }
    }

    if (f.type === 'tel' && !isUsablePhone(v.trim())) {
      errors[f.id] = 'Please enter a valid phone number.';
    }

    if (f.type === 'stars' && !/^[1-5]$/.test(v)) {
      errors[f.id] = 'Please pick a star rating.';
    }

    if (f.maxLength !== undefined && v.length > f.maxLength) {
      errors[f.id] = `Please keep this under ${f.maxLength} characters.`;
    }
  }

  return errors;
}

/**
 * Build the wire-keyed answers payload from visible fields only.
 * - checkbox: array of chosen option values
 * - allowOther custom entry: "Other: <text>"
 * - emit "valueLabel": "value | label" when the chosen option has a distinct label
 * - everything else: trimmed string
 */
export function buildWireAnswers(
  def: FormDefinition,
  visibleItems: Set<string>,
  answers: Answers,
  otherText: Record<string, string>,
): Record<string, string | string[]> {
  const out: Record<string, string | string[]> = {};

  for (const f of allFields(def)) {
    if (!visibleItems.has(f.id)) continue;
    const v = answers[f.id];

    if (f.type === 'checkbox') {
      out[f.wire] = Array.isArray(v) ? [...v] : [];
      continue;
    }

    let s = typeof v === 'string' ? v.trim() : '';
    if (f.allowOther && s === OTHER) {
      s = `Other: ${(otherText[f.id] ?? '').trim()}`;
    } else if (f.emit === 'valueLabel' && s !== '') {
      const opt = f.options?.find((o) => o.value === s);
      if (opt?.label && opt.label !== opt.value) s = `${opt.value} | ${opt.label}`;
    }
    out[f.wire] = s;
  }

  return out;
}
